import React, { useMemo, useState } from 'react';
import './Citation.css';
import { FaExternalLinkAlt } from 'react-icons/fa';

type CopyState = 'idle' | 'copied' | 'error';

const Citation: React.FC = () => {
  const pacaCitation = useMemo(
    () =>
      `Scholz-Murcia, N., Rodríguez-Mena, A., Madarnás-Gómez, V., & Monleón-Getino, A. (2026). PACA: An accessible solution for paleocoordinate calculation. University of Barcelona.`,
    []
  );

  const palaeoverseCitation = useMemo(
    () =>
      `Jones, L. A., Gearty, W., Allen, B. J., Eichenseer, K., Dean, C. D., Galván, S., Chiarenza, A. A. (2023). Palaeoverse: A community-driven R package to support palaeobiological analysis. Methods in Ecology and Evolution, 14(9). https://doi.org/10.1111/2041-210X.14099`,
    []
  );

  const paleomapCitation = useMemo(
    () =>
      `Scotese, C. R. (2016). PALEOMAP PaleoAtlas for GPlates and the PaleoData Plotter Program. PALEOMAP Project. http://www.earthbyte.org/paleomap--paleoatlas--for--gplates`,
    []
  );

  const [copy, setCopy] = useState<Record<string, CopyState>>({
    paca: 'idle',
    palaeoverse: 'idle',
    paleomap: 'idle',
  });

  const copyToClipboard = async (key: keyof typeof copy, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopy((s) => ({ ...s, [key]: 'copied' }));
      window.setTimeout(() => setCopy((s) => ({ ...s, [key]: 'idle' })), 1200);
    } catch {
      setCopy((s) => ({ ...s, [key]: 'error' }));
      window.setTimeout(() => setCopy((s) => ({ ...s, [key]: 'idle' })), 1500);
    }
  };

  const copyLabel = (state: CopyState) =>
    state === 'copied' ? 'Copied' : state === 'error' ? 'Copy failed' : 'Copy';

  return (
    <div className="citation">
      <header className="hero">
        <div className="hero-inner">
          <p className="hero-kicker">PACA</p>
          <h1 className="hero-title">Citation</h1>
          <p className="hero-subtitle">
            Copy ready-to-use references for publications, supplementary material, and figure captions.
          </p>
        </div>
      </header>

      <main className="wrap">
        <section className="section">
          <div className="sectionHead">
            <h2>How to cite</h2>
            <p>Use the blocks below depending on what you used from the platform.</p>
          </div>

          <div className="grid">
            {/* PACA */}
            <article className="citeCard">
              <div className="citeCardHead">
                <div>
                  <h3>PACA (website output)</h3>
                  <p className="muted">Use when reporting paleocoordinates produced by PACA.</p>
                </div>
              </div>

              <pre className="bib" aria-label="PACA citation"><code>{pacaCitation}</code></pre>

              <div className="actions">
                <button
                  type="button"
                  className="btnPrimary"
                  onClick={() => copyToClipboard('paca', pacaCitation)}
                >
                  {copyLabel(copy.paca)}
                </button>
              </div>
            </article>

            {/* Palaeoverse */}
            <article className="citeCard">
              <div className="citeCardHead">
                <div>
                  <h3>Models & methodology</h3>
                  <p className="muted">Use when describing plate models / reconstruction method.</p>
                </div>
              </div>

              <pre className="bib" aria-label="Palaeoverse citation"><code>{palaeoverseCitation}</code></pre>

              <div className="actions">
                <button
                  type="button"
                  className="btnPrimary"
                  onClick={() => copyToClipboard('palaeoverse', palaeoverseCitation)}
                >
                  {copyLabel(copy.palaeoverse)}
                </button>

                <a
                  className="btnSoft"
                  href="https://doi.org/10.1111/2041-210X.14099"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  DOI <FaExternalLinkAlt aria-hidden="true" />
                </a>
              </div>
            </article>

            {/* PALEOMAP */}
            <article className="citeCard">
              <div className="citeCardHead">
                <div>
                  <h3>Paleomap visualizations</h3>
                  <p className="muted">Use when exporting/using PALEOMAP-based maps from PACA.</p>
                </div>
              </div>

              <pre className="bib" aria-label="PALEOMAP citation"><code>{paleomapCitation}</code></pre>

              <div className="actions">
                <button
                  type="button"
                  className="btnPrimary"
                  onClick={() => copyToClipboard('paleomap', paleomapCitation)}
                >
                  {copyLabel(copy.paleomap)}
                </button>

                <a
                  className="btnSoft"
                  href="http://www.earthbyte.org/paleomap--paleoatlas--for--gplates"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Source <FaExternalLinkAlt aria-hidden="true" />
                </a>
              </div>
            </article>
          </div>
        </section>

        <section className="section">
          <div className="sectionHead">
            <h2>Meet the team</h2>
            <p>Contributions and roles.</p>
          </div>

          <div className="teamGrid">
            <article className="person">
              <div className="avatar" aria-hidden="true">NS</div>
              <h3>Noa Scholz-Murcia</h3>
              <p className="role">Project Lead · R Developer</p>
              <p className="muted">
                PhD student in Statistics at the University of Barcelona. Conceptualization, development and implementation
                of the palaeorotate function, drafting of the manuscript, and preparation of figures and tables.
              </p>
            </article>

            <article className="person">
              <div className="avatar avatarTeal" aria-hidden="true">AR</div>
              <h3>Alejandro Rodríguez-Mena</h3>
              <p className="role">Web Developer · Data Engineer</p>
              <p className="muted">
                Design and programming of the web interface, integration of the computational backend with the UI,
                testing, debugging, and co-writing part of the Methods section.
              </p>
            </article>

            <article className="person">
              <div className="avatar avatarBlue" aria-hidden="true">VM</div>
              <h3>Víctor Madarnás-Gómez</h3>
              <p className="role">Supporting Developer</p>
              <p className="muted">
                Improvements and support to the web interface, testing, debugging,
                and co-writing part of the Methods section.
              </p>
            </article>

            <article className="person">
              <div className="avatar avatarAmber" aria-hidden="true">AM</div>
              <h3>Antonio Monleón-Getino</h3>
              <p className="role">Statistical Advisor · Scientific Contributor</p>
              <p className="muted">
                Supervision of the project, guidance on study design, manuscript revision,
                and final approval of the submitted version.
              </p>
            </article>
          </div>

          <div className="note">
            All authors have read and approved the final manuscript.
          </div>
        </section>
      </main>
    </div>
  );
};

export default Citation;
